import React, { useState, useEffect } from 'react';
import { Clock, ArrowLeft, User, FileSignature, Shield, Upload, Globe, Download } from 'lucide-react';
import { documentService } from '../services/documentService';
import { API_URL } from '../utils/apiConfig';

// Apple-inspired design system matching Dashboard.jsx
const colors = {
  accent: '#1e40af',
  accentHover: '#1e3a8a',
  accentLight: 'rgba(30, 64, 175, 0.1)',
  white: '#ffffff',
  gray50: '#f9f9f9',
  gray100: '#f3f3f3',
  gray200: '#e6e6e6',
  gray300: '#d1d1d1',
  gray500: '#8e8e8e',
  gray700: '#636363',
  gray900: '#333333',
  success: 'rgb(52, 199, 89)',
  warning: 'rgb(255, 149, 0)',
  error: 'rgb(255, 59, 48)',
  successLight: 'rgba(52, 199, 89, 0.1)',
  warningLight: 'rgba(255, 149, 0, 0.1)',
  errorLight: 'rgba(255, 59, 48, 0.1)',
};

const fontStyles = {
  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
  heading: {
    fontWeight: 600,
    letterSpacing: '-0.03em',
  },
  body: {
    fontWeight: 400,
    letterSpacing: '-0.01em',
  },
  medium: {
    fontWeight: 500,
  },
};

const DocumentVersionHistory = ({ documentId, onClose, onRestoreVersion }) => {
  const [auditEvents, setAuditEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  
  useEffect(() => {
    const fetchAuditTrail = async () => {
      try {
        setIsLoading(true);
        const events = await documentService.getDocumentAuditTrail(documentId);
        setAuditEvents(events);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAuditTrail();
  }, [documentId]);
  
  const getEventIcon = (type) => {
    switch (type) {
      case 'document_created': return <Upload size={16} style={{color: colors.accent}} />;
      case 'document_signed': return <FileSignature size={16} style={{color: colors.success}} />;
      case 'permission_granted': return <Shield size={16} style={{color: '#8b5cf6'}} />;
      default: return <Clock size={16} style={{color: colors.gray500}} />;
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Download audit trail PDF - following exact pattern from DocumentViewer.jsx
  const handleDownloadAuditTrail = async () => {
    try {
      console.log('📋 Starting audit trail PDF download for document:', documentId);
      setIsDownloading(true);
      
      // Get the signature token from URL if present (same pattern as existing downloads)
      const params = new URLSearchParams(window.location.search);
      const signatureToken = params.get('token');
      
      // Build download URL
      let downloadUrl = `${API_URL}/documents/${documentId}/audit-trail/download`;
      if (signatureToken) {
        downloadUrl += `?token=${encodeURIComponent(signatureToken)}`;
      }
      
      // Set up headers (same pattern as DocumentViewer.jsx)
      const headers = {};
      if (!signatureToken && localStorage.getItem('token')) {
        headers['Authorization'] = `Bearer ${localStorage.getItem('token')}`;
      }
      
      // Fetch the PDF
      const response = await fetch(downloadUrl, {
        method: 'GET',
        headers: headers
      });
      
      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }
      
      // Get the blob
      const blob = await response.blob();
      
      // Get filename from response headers or use default (same pattern as existing downloads)
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'audit-trail.pdf';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      // Create download link (exact same pattern as DocumentViewer.jsx)
      const url = window.URL.createObjectURL(blob);
      const link = window.document.createElement('a');
      link.href = url;
      link.download = filename;
      
      // Trigger download
      window.document.body.appendChild(link);
      link.click();
      
      // Cleanup
      window.document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('✅ Audit trail PDF downloaded successfully:', filename);
      
    } catch (error) {
      console.error('❌ Error downloading audit trail PDF:', error);
      alert('Failed to download audit trail PDF. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };
  
  return (
    <div 
      className="rounded-3xl border shadow-xl max-w-5xl w-full mx-auto"
      style={{
        fontFamily: fontStyles.fontFamily,
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 250, 252, 0.9) 50%, rgba(241, 245, 249, 0.85) 100%)',
        borderColor: colors.gray200,
        backdropFilter: 'blur(20px) saturate(180%)',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.12), 0 10px 20px -5px rgba(0, 0, 0, 0.08)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      }}
    >
      {/* Header */}
      <div 
        className="flex items-center justify-between p-8 border-b"
        style={{
          borderColor: 'rgba(148, 163, 184, 0.15)',
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.7))',
          backdropFilter: 'blur(12px)',
          borderRadius: '24px 24px 0 0'
        }}
      >
        <div>
          <h2 
            className="text-3xl font-bold flex items-center"
            style={{
              color: colors.gray900,
              ...fontStyles.heading,
              letterSpacing: '-0.03em',
              lineHeight: '1.2'
            }}
          >
            <Clock className="mr-3" size={28} style={{color: colors.accent}} />
            Audit Trail
          </h2>
          <div 
            className="px-3 py-1 rounded-full text-sm font-medium mt-2 inline-block"
            style={{
              backgroundColor: colors.accentLight,
              color: colors.accent,
              ...fontStyles.medium
            }}
          >
            Document Activity
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {/* Download Audit Trail Button */}
          <button
            onClick={handleDownloadAuditTrail}
            disabled={isDownloading}
            className="px-6 py-3 rounded-2xl font-medium transition-all duration-300 ease-out border group flex items-center"
            style={{
              background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentHover})`,
              color: colors.white,
              borderColor: colors.accent,
              ...fontStyles.medium,
              letterSpacing: '-0.01em',
              boxShadow: '0 3px 12px rgba(30, 64, 175, 0.2)',
              textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
              opacity: isDownloading ? 0.7 : 1,
              cursor: isDownloading ? 'not-allowed' : 'pointer'
            }}
            onMouseEnter={(e) => {
              if (!isDownloading) {
                e.target.style.transform = 'translateY(-1px)';
                e.target.style.boxShadow = '0 6px 20px rgba(30, 64, 175, 0.25)';
                e.target.style.filter = 'brightness(1.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (!isDownloading) {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 3px 12px rgba(30, 64, 175, 0.2)';
                e.target.style.filter = 'brightness(1)';
              }
            }}
          >
            {isDownloading ? (
              <>
                <div 
                  className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"
                />
                Generating PDF...
              </>
            ) : (
              <>
                <Download size={18} className="mr-2 transition-transform duration-300 group-hover:translate-y-0.5" />
                Download Audit Trail
              </>
            )}
          </button>
          
          {/* Back to Document Button */}
          <button
            onClick={onClose}
            className="px-6 py-3 rounded-2xl font-medium transition-all duration-300 ease-out border group flex items-center"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.8))',
              color: colors.gray700,
              borderColor: colors.gray200,
              ...fontStyles.medium,
              letterSpacing: '-0.01em',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.8)'
            }}
            onMouseEnter={(e) => {
              e.target.style.background = 'linear-gradient(135deg, rgba(248, 250, 252, 0.9), rgba(241, 245, 249, 0.8))';
              e.target.style.color = colors.gray900;
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.9)';
              e.target.style.borderColor = colors.gray300;
            }}
            onMouseLeave={(e) => {
              e.target.style.background = 'linear-gradient(135deg, rgba(255, 255, 255, 0.9), rgba(248, 250, 252, 0.8))';
              e.target.style.color = colors.gray700;
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.8)';
              e.target.style.borderColor = colors.gray200;
            }}
          >
            <ArrowLeft size={18} className="mr-2 transition-transform duration-300 group-hover:-translate-x-0.5" />
            Back to Document
          </button>
        </div>
      </div>
      
      {/* Content */}
      <div className="flex-1 overflow-y-auto p-8" style={{maxHeight: '70vh'}}>
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div 
              className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2"
              style={{borderColor: `${colors.accent} transparent ${colors.accent} ${colors.accent}`}}
            />
          </div>
        ) : error ? (
          <div 
            className="text-center py-12 rounded-2xl border p-8"
            style={{
              backgroundColor: colors.errorLight,
              borderColor: colors.error,
              color: colors.error
            }}
          >
            <div className="font-medium mb-2">Error loading audit trail</div>
            <div className="text-sm">{error}</div>
          </div>
        ) : auditEvents.length === 0 ? (
          <div className="text-center py-12">
            <Clock size={48} className="mx-auto mb-4" style={{color: colors.gray300}} />
            <div style={{color: colors.gray500, ...fontStyles.body}}>No audit events found</div>
          </div>
        ) : (
          <div className="space-y-6">
            {auditEvents.map((event) => (
              <div 
                key={event.id} 
                className="p-6 rounded-2xl border transition-all duration-200 ease-out cursor-pointer group"
                style={{
                  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.8))',
                  borderColor: colors.gray200,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.8)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.08), inset 0 1px 0 rgba(255, 255, 255, 0.9)';
                  e.currentTarget.style.borderColor = colors.gray300;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.04), inset 0 1px 0 rgba(255, 255, 255, 0.8)';
                  e.currentTarget.style.borderColor = colors.gray200;
                }}
              >
                <div className="flex items-start space-x-4">
                  <div 
                    className="flex-shrink-0 mt-1 p-3 rounded-2xl"
                    style={{
                      background: `linear-gradient(135deg, ${colors.accentLight}, rgba(30, 64, 175, 0.05))`,
                      boxShadow: '0 4px 12px rgba(30, 64, 175, 0.1)'
                    }}
                  >
                    {getEventIcon(event.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3 mb-2">
                      <span 
                        className="font-semibold"
                        style={{
                          color: colors.gray900,
                          ...fontStyles.medium,
                          fontSize: '16px'
                        }}
                      >
                        {event.user?.name || 'Unknown User'}
                      </span>
                      <span 
                        className="text-sm px-3 py-1 rounded-full"
                        style={{
                          color: colors.gray600,
                          backgroundColor: colors.gray100,
                          ...fontStyles.body,
                          fontSize: '14px'
                        }}
                      >
                        {event.details}
                      </span>
                    </div>
                    <div className="space-y-2" style={{color: colors.gray500, ...fontStyles.body}}>
                      <div className="flex items-center space-x-2">
                        <Clock size={14} style={{color: colors.gray400}} />
                        <span className="text-sm">{formatTimestamp(event.timestamp)}</span>
                      </div>
                      {event.ipAddress && (
                        <div className="flex items-center space-x-2">
                          <Globe size={14} style={{color: colors.gray400}} />
                          <span className="text-sm">IP: {event.ipAddress}</span>
                        </div>
                      )}
                      {event.user?.email && (
                        <div className="flex items-center space-x-2">
                          <User size={14} style={{color: colors.gray400}} />
                          <span className="text-sm">{event.user.email}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentVersionHistory;